import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Formik, Field, Form, ErrorMessage } from "formik";
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmationModal from '../../shared/ConfirmationModal';
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import NewconfirmationModal from '../../shared/NewconfirmationModal';
import AllFailureModal from '../../shared/AllFailureModal';

interface Degree {
  d_id: string | number;
  degree_name: string;
}

const DegreeEdit: React.FC = () => {
  const { d_id } = useParams<{ d_id: string }>();
  const [degreeData, setDegreeData] = useState<Degree>({ d_id: d_id || '', degree_name: '' });
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<string | null>(null);
  const [updatedValues, setUpdatedValues] = useState<Degree | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const navigate = useNavigate();

  const headers = {
    "x-access-token": localStorage.getItem("jwttoken") || "",
  };

  const fetchDegreeById = async (id: string) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}degree/getdegreebydid/${d_id}`);
      return response.data[0];
    } catch (error) {
      throw new Error("Error fetching degree data");
    }
  };

  const updateDegreeApi = async (data: Degree) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}degree/updatedegree`, data, { headers });
      setSuccessMessage("Degree updated successfully!");
      setShowSuccessModal(true);
      setTimeout(() => navigate('/degreelist'), 3000);
    } catch (error: any) {
      if (error.response?.status === 409) {
        setFailureMessage("Degree name already exists.");
      } else {
        setFailureMessage("Error updating degree.");
      }
      setShowFailureModal(true);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        if (d_id) {
          const data = await fetchDegreeById(d_id);
          setDegreeData(data);
        }
      } catch (err) {
        setFailureMessage("Failed to load degree data.");
        setShowFailureModal(true);
      }
    };
    loadData();
  }, [d_id]);

  const handleSubmit = (values: Degree) => {
    if (JSON.stringify(values) === JSON.stringify(degreeData)) {
      setModalAction('noUpdate');
      setSuccessMessage("No updates were made.");
      setShowSuccessModal(true);
    } else {
      setUpdatedValues(values);
      setModalAction('OK');
      setShowModal(true);
    }
  };

  const handleConfirm = async () => {
    if (modalAction === 'OK' && updatedValues) {
      await updateDegreeApi(updatedValues);
    }
    setShowModal(false);
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Edit Degree</h4>
            </Card.Header>
            <Card.Body>
              <Formik initialValues={degreeData} enableReinitialize onSubmit={handleSubmit}>
                {() => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="d_id" className="form-label">Degree ID:</label>
                        <Field type="text" name="d_id" className="form-control" disabled />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="degree_name" className="form-label">Degree Name:</label>
                        <Field name="degree_name" className="form-control" type="text" />
                        <ErrorMessage name="degree_name" component="div" className="text-danger small mt-1" />
                      </Col>
                    </Row>

                    <div className="text-center d-flex justify-content-center gap-2">
                      <Button type="submit" variant="success">Update</Button>
                      <Button variant="danger" onClick={() => navigate('/degreelist')}>Go Back</Button>
                    </div>
                  </Form>
                )}
              </Formik>

              <ConfirmationModal
                show={showModal}
                onHide={() => setShowModal(false)}
                modalMessage={modalAction === 'noUpdate' ? "No updates were made." : "Are you sure you want to save changes?"}
                operationType={modalAction}
                confirmation={handleConfirm}
              />
              <NewconfirmationModal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                modalMessage={successMessage}
                operationType="OK"
                timeoutDuration={3000}
              />
              <AllFailureModal
                show={showFailureModal}
                onHide={() => setShowFailureModal(false)}
                modalMessage={failureMessage}
                operationType="OK"
                timeoutDuration={3000}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DegreeEdit;
